import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, AlertCircle, Activity, Server, Database } from 'lucide-react';

const SystemStatus = () => {
  const [services, setServices] = useState([
    { name: 'API Gateway', status: 'operational', uptime: '99.99%', lastChecked: '2 mins ago' },
    { name: 'SMS Gateway', status: 'operational', uptime: '99.95%', lastChecked: '1 min ago' },
    { name: 'Database', status: 'operational', uptime: '99.98%', lastChecked: '30 secs ago' },
    { name: 'Kafka Queue', status: 'degraded', uptime: '99.90%', lastChecked: '1 min ago' },
    { name: 'Redis Cache', status: 'operational', uptime: '99.99%', lastChecked: '45 secs ago' },
    { name: 'SMPP Server', status: 'operational', uptime: '99.97%', lastChecked: '2 mins ago' },
  ]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'operational':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'degraded':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'down':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Activity className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'operational':
        return 'text-green-600 bg-green-100';
      case 'degraded':
        return 'text-yellow-600 bg-yellow-100';
      case 'down':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Activity className="w-6 h-6" />
          System Status
        </h1>
        <p className="text-gray-600 mt-1">Real-time status of all Way2Connect services</p>
      </div>

      <div className="mb-6 bg-white p-4 rounded-lg shadow border border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            <span className="font-semibold text-gray-900">All Systems Operational</span>
            <span className="text-sm text-gray-500">(with minor issues)</span>
          </div>
          <button className="text-sm text-indigo-600 hover:text-indigo-700">
            Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {services.map((service, index) => (
          <div key={index} className="bg-white p-6 rounded-lg shadow border border-gray-200">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                {getStatusIcon(service.status)}
                <h3 className="font-semibold text-gray-900">{service.name}</h3>
              </div>
              <span className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusColor(service.status)}`}>
                {service.status}
              </span>
            </div>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Uptime:</span>
                <span className="font-medium text-gray-900">{service.uptime}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Last checked:</span>
                <span className="text-gray-500">{service.lastChecked}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Server className="w-5 h-5" />
            Service Metrics
          </h2>
          <div className="space-y-3">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">API Response Time</span>
                <span className="font-medium text-gray-900">120ms</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-green-500 h-2 rounded-full" style={{ width: '75%' }}></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">SMS Throughput</span>
                <span className="font-medium text-gray-900">850/s</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-yellow-500 h-2 rounded-full" style={{ width: '85%' }}></div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Database className="w-5 h-5" />
            Recent Incidents
          </h2>
          <div className="space-y-2">
            <div className="p-3 bg-yellow-50 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <AlertCircle className="w-4 h-4 text-yellow-600" />
                <span className="text-sm font-medium text-gray-900">Kafka Queue Degradation</span>
              </div>
              <p className="text-xs text-gray-600">Resolved in 15 minutes - 2 hours ago</p>
            </div>
            <div className="p-3 bg-green-50 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-gray-900">Scheduled Maintenance</span>
              </div>
              <p className="text-xs text-gray-600">Completed successfully - Yesterday</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemStatus;
